import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import {
  Add, Search, MoreHoriz, EditOutlined, DeleteOutline, ToggleOnOutlined, ToggleOffOutlined, MeetingRoomOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { roomService, Room } from '../../../services/roomService';
import { useDebounce } from '../../../hooks/useDebounce';
import FilterBar from '../../common/FilterBar';
import SearchableSelect from '../../common/SearchableSelect';
import EmptyState from '../../common/EmptyState';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import { ToneChip } from '../../common/StatusChip';
import { ROOM_TYPE_LABELS } from '../timetableUtils';
import RoomFormDialog from './RoomFormDialog';

const TYPE_OPTIONS = Object.entries(ROOM_TYPE_LABELS).map(([value, label]) => ({ value, label }));
const STATUS_OPTIONS = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }];

/** The school's rooms. Special rooms (labs, computer lab…) are what lessons can ask for in Lessons. */
const RoomsTab: React.FC<{ canWrite: boolean }> = ({ canWrite }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; room: Room } | null>(null);
  const [editing, setEditing] = useState<Room | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Room | null>(null);
  const [busy, setBusy] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const request = useRef(0);

  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    try {
      const data = await roomService.getRooms({
        page: page + 1,
        limit: rowsPerPage,
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
        ...(type && { type }),
        ...(status && { isActive: status === 'active' }),
      });
      if (id !== request.current) return;
      setRooms(data?.rooms || []);
      setTotal(data?.pagination?.totalItems || 0);
    } catch (err: any) {
      if (id === request.current) enqueueSnackbar(err?.message || 'Could not load rooms', { variant: 'error' });
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, type, status, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [debouncedSearch, type, status]);

  const toggleActive = useCallback(async (room: Room) => {
    setMenu(null);
    try {
      await roomService.updateRoom(room._id, { isActive: !room.isActive });
      enqueueSnackbar(room.isActive ? 'Room deactivated' : 'Room activated', { variant: 'success' });
      load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not update the room', { variant: 'error' });
    }
  }, [enqueueSnackbar, load]);

  const confirmDelete = useCallback(async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await roomService.deleteRoom(deleting._id);
      enqueueSnackbar('Room deleted', { variant: 'success' });
      setDeleting(null);
      load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not delete the room', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }, [deleting, enqueueSnackbar, load]);

  const filtered = !!(debouncedSearch.trim() || type || status);
  const empty = !loading && rooms.length === 0;
  const columns = canWrite ? 5 : 4;

  const addButton = useMemo(() => canWrite && (
    <Button variant="contained" startIcon={<Add />} onClick={() => setEditing(null)} sx={{ ml: 'auto' }}>Add room</Button>
  ), [canWrite]);

  return (
    <Box>
      <FilterBar loading={loading && rooms.length > 0}>
        <TextField
          size="small"
          placeholder="Search rooms"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, maxWidth: 360 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
        />
        <SearchableSelect label="Kind" placeholder="All kinds" value={type} onChange={setType} options={TYPE_OPTIONS} minWidth={170} />
        <SearchableSelect label="Status" placeholder="Any status" value={status} onChange={setStatus} options={STATUS_OPTIONS} minWidth={140} />
        {addButton}
      </FilterBar>

      {empty ? (
        <EmptyState
          icon={<MeetingRoomOutlined />}
          title={filtered ? 'No rooms match' : 'No rooms yet'}
          description={filtered
            ? 'Try another search or clear the filters.'
            : 'Add labs, the computer room and other special rooms so lessons that need them can be booked into them.'}
          action={!filtered && canWrite ? { label: 'Add room', icon: <Add />, onClick: () => setEditing(null) } : undefined}
        />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', bgcolor: 'action.hover' } }}>
                  <TableCell>Room</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell align="right">Seats</TableCell>
                  <TableCell>Status</TableCell>
                  {canWrite && <TableCell width={48} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && rooms.length === 0 ? <TableRowSkeletons columns={columns} rows={5} /> : rooms.map((room) => (
                  <TableRow key={room._id} hover sx={{ opacity: room.isActive ? 1 : 0.6 }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{room.name}</Typography>
                      {!!(room.description || room.facilities?.length) && (
                        <Typography variant="caption" color="text.secondary">
                          {[room.description, room.facilities?.join(', ')].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <ToneChip label={ROOM_TYPE_LABELS[room.type] ?? room.type} tone={room.type === 'classroom' ? 'neutral' : 'highlight'} />
                    </TableCell>
                    <TableCell align="right"><Typography variant="body2">{room.capacity}</Typography></TableCell>
                    <TableCell><ToneChip label={room.isActive ? 'Active' : 'Inactive'} tone={room.isActive ? 'success' : 'neutral'} /></TableCell>
                    {canWrite && (
                      <TableCell>
                        <IconButton size="small" aria-label="Room actions" onClick={(e) => setMenu({ anchor: e.currentTarget, room })}>
                          <MoreHoriz fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Rooms per page:"
          />
        </Paper>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setEditing(menu!.room); setMenu(null); }}>
          <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon><ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => toggleActive(menu!.room)}>
          <ListItemIcon>{menu?.room.isActive ? <ToggleOffOutlined fontSize="small" /> : <ToggleOnOutlined fontSize="small" />}</ListItemIcon>
          <ListItemText>{menu?.room.isActive ? 'Deactivate' : 'Activate'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setDeleting(menu!.room); setMenu(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon><ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <RoomFormDialog
        open={editing !== undefined}
        room={editing ?? null}
        onClose={() => setEditing(undefined)}
        onSaved={() => { setEditing(undefined); load(); }}
      />
      <ConfirmationDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        loading={busy}
        title="Delete room"
        message={`Delete ${deleting?.name ?? 'this room'}? Lessons that need this kind of room will use the others.`}
        confirmText="Delete"
      />
    </Box>
  );
};

export default RoomsTab;
